import {
  Box,
  Flex,
  Icon,
  IconButton,
  LinkBox,
  LinkOverlay,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { FiClock, FiStar } from 'react-icons/fi';
import { Link } from 'react-router-dom';

import { BiReport } from '../../../services/biService';
import { BiAppearance } from '../../../utils/biAppearance';

interface Props {
  report: BiReport;
  appearance: BiAppearance;
  onToggleFavorite?: (report: BiReport) => void;
  showGroup?: boolean;
}

// Mesmo visual dos cards do menu principal (MenuList): superfície gray.800,
// raio 12px, borda sutil e elevação no hover. A cor e o ícone vêm do grupo
// (setor) ou do próprio relatório, para diferenciar os setores. O card inteiro
// é clicável (LinkBox) sem colocar o botão de favorito dentro do link.
export function ReportCard({
  report,
  appearance,
  onToggleFavorite,
  showGroup = true,
}: Props) {
  const { color, icon } = appearance;
  const text = report.summary || report.description;

  const favoriteLabel = report.isFavorite
    ? 'Remover dos favoritos'
    : 'Adicionar aos favoritos';

  const starIcon = (
    <Icon as={FiStar} fill={report.isFavorite ? 'currentColor' : 'none'} />
  );

  return (
    <LinkBox
      as="article"
      h="100%"
      display="flex"
      flexDirection="column"
      bg="gray.800"
      borderWidth="1px"
      borderColor="gray.700"
      borderTopWidth="4px"
      borderTopColor={`${color}.400`}
      borderRadius="12px"
      boxShadow="sm"
      p="4"
      pr={onToggleFavorite ? '10' : '4'}
      transition="all 0.3s"
      _hover={{ transform: 'translateY(-4px)', boxShadow: 'md' }}
      _active={{ transform: 'translateY(-1px)' }}
      _focusWithin={{ boxShadow: 'outline' }}
    >
      <Flex align="flex-start" gridGap="3" flex="1">
        <Flex
          w="10"
          h="10"
          flexShrink={0}
          borderRadius="full"
          bg={`${color}.100`}
          color={`${color}.600`}
          align="center"
          justify="center"
        >
          <Icon as={icon} boxSize="5" />
        </Flex>

        <Box minW="0">
          <LinkOverlay
            as={Link}
            to={`/bi-management/renderization/${report.id}`}
            _focus={{ outline: 'none' }}
          >
            <Text fontWeight="medium" color="gray.50" noOfLines={2}>
              {report.reportName}
            </Text>
          </LinkOverlay>
          {showGroup && (
            <Text fontSize="xs" color={`${color}.400`} mt="0.5" noOfLines={1}>
              {report.groupNameSamasc || 'Sem grupo'}
            </Text>
          )}
          {text && (
            <Text fontSize="sm" color="gray.400" mt="2" noOfLines={2}>
              {text}
            </Text>
          )}
        </Box>
      </Flex>

      {report.refresh_frequency && (
        <Flex
          align="center"
          gridGap="1"
          mt="3"
          pl="52px"
          fontSize="xs"
          color="gray.500"
        >
          <Icon as={FiClock} />
          <Text noOfLines={1}>{report.refresh_frequency}</Text>
        </Flex>
      )}

      {/* O IconButton do Chakra mantém `position: relative`; a caixa é que
          fixa a estrela no canto e a deixa acima do link do card. */}
      {onToggleFavorite && (
        <Box position="absolute" top="2" right="2" zIndex={1}>
          <Tooltip label={favoriteLabel} hasArrow>
            <IconButton
              aria-label={favoriteLabel}
              aria-pressed={report.isFavorite}
              icon={starIcon}
              size="sm"
              variant="ghost"
              color={report.isFavorite ? 'yellow.400' : 'gray.400'}
              onClick={() => onToggleFavorite(report)}
            />
          </Tooltip>
        </Box>
      )}
    </LinkBox>
  );
}
